import { contentBlocksAdapter } from "@/services/contentful/assemblies/page/adapter/content-blocks.adapter";
import { getBlockId } from "@/services/contentful/assemblies/page/adapter/utils";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { AccordionPageBlock, PageBlock } from "@/types/page-block";

type ContentfulColumnWidth = "narrow" | "medium" | "wide";

export interface AccordionItemInput {
  sys?: { id: string };
  __typename: "AccordionItemD2C";
  internalName: string;
  label?: string | null;
  contentBlocksCollection?: {
    items: Array<{ __typename?: string } | null>;
  } | null;
}

export interface AccordionInput {
  sys?: { id: string };
  __typename: "AccordionD2C";
  internalName: string;
  columnWidth?: ContentfulColumnWidth | null;
  itemsCollection: {
    items: AccordionItemInput[];
  };
  uiConfiguration?: UiConfigurationInput;
}

const NARROW_COLUMN_WIDTH = 8;
const MEDIUM_COLUMN_WIDTH = 10;
const WIDE_COLUMN_WIDTH = 12;

const COLUMN_WIDTH_MAP = {
  narrow: NARROW_COLUMN_WIDTH,
  medium: MEDIUM_COLUMN_WIDTH,
  wide: WIDE_COLUMN_WIDTH,
} as const;

type AccordionItemPageBlock = AccordionPageBlock["items"][number];

function normalizeColumnWidth(
  value: AccordionInput["columnWidth"]
): AccordionPageBlock["columnWidth"] {
  const normalizedValue = value?.trim().toLowerCase() as
    | ContentfulColumnWidth
    | undefined;

  if (!normalizedValue) {
    return MEDIUM_COLUMN_WIDTH;
  }

  return COLUMN_WIDTH_MAP[normalizedValue] ?? MEDIUM_COLUMN_WIDTH;
}

function adaptAccordionItem(
  item: AccordionItemInput,
  index: number,
  locale?: string,
  basePath?: string
): AccordionItemPageBlock {
  const rawChildren = item.contentBlocksCollection?.items ?? [];
  const children = contentBlocksAdapter(
    rawChildren as Parameters<typeof contentBlocksAdapter>[0],
    locale,
    basePath
  );

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: "AccordionItemD2C",
    internalName: item.internalName,
    label: item.label ?? item.internalName,
    children: children as PageBlock[],
  };
}

export function AccordionAdapter(
  item: AccordionInput,
  index: number,
  locale?: string,
  basePath?: string
): AccordionPageBlock {
  const rawItems = item.itemsCollection.items;

  const items = rawItems.map((entry, itemIndex) =>
    adaptAccordionItem(entry, itemIndex, locale, basePath)
  );

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: "AccordionD2C",
    internalName: item.internalName,
    columnWidth: normalizeColumnWidth(item.columnWidth),
    items,
    uiConfiguration: item.uiConfiguration
      ? UiConfigurationAdapter(item.uiConfiguration)
      : undefined,
  };
}
